import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * BrowserMultipleWindow component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconBrowserMultipleWindow extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.74528 2.84245C6.89733 2.6904 7.10355 2.60498 7.31858 2.60498H20.5851C20.8001 2.60498 21.0064 2.6904 21.1584 2.84245C21.3105 2.9945 21.3959 3.20072 21.3959 3.41575V5.78735H6.50781V3.41575C6.50781 3.20072 6.59323 2.9945 6.74528 2.84245ZM5.00781 6.53735V3.41575C5.00781 2.8029 5.25127 2.21514 5.68462 1.78179C6.11797 1.34844 6.70573 1.10498 7.31858 1.10498H20.5851C21.198 1.10498 21.7857 1.34844 22.2191 1.78179C22.6524 2.21514 22.8959 2.8029 22.8959 3.41575V6.53735V15.9019C22.8959 16.5148 22.6524 17.1025 22.2191 17.5359C21.7857 17.9692 21.198 18.2127 20.5851 18.2127H18.244C17.8298 18.2127 17.494 17.8769 17.494 17.4627C17.494 17.0485 17.8298 16.7127 18.244 16.7127H20.5851C20.8001 16.7127 21.0064 16.6273 21.1584 16.4752C21.3105 16.3232 21.3959 16.1169 21.3959 15.9019V7.28735H6.50781V8.87844C6.50781 9.29265 6.17203 9.62844 5.75781 9.62844C5.3436 9.62844 5.00781 9.29265 5.00781 8.87844V6.53735Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.60547 14.341C2.60547 13.8933 2.96846 13.5303 3.41624 13.5303H12.7809C13.2286 13.5303 13.5916 13.8933 13.5916 14.341V15.9323H2.60547V14.341ZM1.10547 16.6823V14.341C1.10547 13.0648 2.14004 12.0303 3.41624 12.0303H12.7809C14.0571 12.0303 15.0916 13.0648 15.0916 14.341V16.6823V20.5841C15.0916 21.8603 14.0571 22.8949 12.7809 22.8949H3.41624C2.14004 22.8949 1.10547 21.8603 1.10547 20.5841V16.6823ZM13.5916 17.4323V20.5841C13.5916 21.0319 13.2286 21.3949 12.7809 21.3949H3.41624C2.96846 21.3949 2.60547 21.0319 2.60547 20.5841V17.4323H13.5916Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-browser-multiple-window') === undefined) {
  customElements.define('cat-icon-browser-multiple-window', BlocksIconBrowserMultipleWindow);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-browser-multiple-window': BlocksIconBrowserMultipleWindow;
  }
}
